import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Close from '@material-ui/icons/Close';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import Box from '@mui/material/Box';
import { getData, getFirst, isValid } from 'clients/Clients';
import { getUserLogGuide, postUserLogGuide } from 'clients/CustomerClient';
import { postCollectVoucher, postVoucherList } from 'clients/PromoClient';
import clsx from 'clsx';
import LoadingBM from 'components-v2/atoms/LoadingBM';
import { ENUM_VOUCHER_COLLECTION_STATUS, IMyDataVoucher, IVendorInfo } from 'components/organisms/PromoCodesContainerV2/interface';
import { COLLECT_VOUCHER_COLLECTED_BLUE, COLLECT_VOUCHER_COLLECTED_GREEN, COLLECT_VOUCHER_COLLECTED_GREY } from 'constants/Images';
import { GUIDE_FEATURE } from 'constants/data/guideline';
import { useAuth } from 'context/Auth';
import { usePopupVoucherContext } from 'context/PopupVoucher';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import DateTimeUtils from 'utils/DateTimeUtils';
import { ImageFallbackStatic } from 'utils/ImageFallback';
import MonitorUtils, { MONITORING_COLLECTOR_TYPE, mapScreenToEnum } from 'utils/MonitorUtils';
import NotifyUtils from 'utils/NotifyUtils';
import CountdownTimerVoucher from '../CountdownTimerVoucher';
import { ENUM_SECTION_PROMO } from './interface';
import styles from './styles.module.css';

interface Props {
  coupons: IMyDataVoucher[];
  headLabel: string;
  actionLabel: string;
  actionIcon: JSX.Element;
  sellerCode: string;
  sellerName: string;
  sku: string;
  skuName: string;
  vendorInfo: IVendorInfo;
}

const timerOut = 15000; // 15s

const SlideCoupon = ({
  coupons,
  headLabel = 'Mã giảm giá có thể sử dụng',
  actionLabel = 'Xem tất cả',
  actionIcon = <PlayCircleOutlineOutlinedIcon />,
  sellerCode,
  sellerName,
  sku,
  skuName,
  vendorInfo,
}: Props) => {
  const router = useRouter();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { user }: any = useAuth();
  const [isOpenDrawer, setOpenDrawer] = useState(false);
  const [couponCollecting, setCouponCollecting] = useState<string | null>(null);
  const [triggerRefetchVouchers, setTriggerRefetchVouchers] = useState(0);
  const [isDragging, setDragging] = useState(false);
  const [isGuideTooltip, toggleGuidedTooltip] = useState(false); // indicator check guide tooltip
  const [isFirstLoading, setFirstLoading] = useState(true); // indicator for first loading
  const [firstEleOfAll, setFirstEleOfAll] = useState<string | null>(null); // first element of array

  // slider settings
  const settingCollectPromoCode = {
    slidesToShow: 3,
    slidesToScroll: 2,
    arrows: false,
    infinite: false,
    autoplay: false,
    beforeChange: () => setDragging(true),
    afterChange: () => setDragging(false),
  };

  const toggleDrawer = () => {
    // handle on Dragging make clicking to child
    if (!isDragging) {
      setOpenDrawer(!isOpenDrawer);
      if (!isOpenDrawer) {
        trackingClickCollectionVoucherSection();
      }
    }
  };

  const handleCollectVoucher = async ({ voucherCode, isPopupCondition = false }: { voucherCode: string; isPopupCondition: boolean }) => {
    setCouponCollecting(voucherCode);
    const respCollected = await postCollectVoucher({
      voucherCode,
      currentPage: router.asPath,
      fullLink: window.location.host,
      screen: mapScreenToEnum({}, router.pathname),
      sku,
    });

    if (respCollected.status === 'OK') {
      setTriggerRefetchVouchers((prev) => prev + 1);
      NotifyUtils.success('Thu thập mã giảm giá thành công');
      if (isPopupCondition) {
        trackingClickCollectionVoucherButtonInDetailPopup(voucherCode);
      } else {
        trackingClickCollectionButton(voucherCode);
      }
    } else {
      NotifyUtils.error('Thu thập mã giảm giá không thành công');
    }
  };

  const handleCloseGuideTooltip = async () => {
    const feature = GUIDE_FEATURE.COLLECTION_VOUCHER_GUIDE;
    await postUserLogGuide({ feature });
    toggleGuidedTooltip(false);
    setFirstEleOfAll(null); // clear state first element when close guide
    document.getElementById('marketPromoSection')?.style.setProperty('overflow-y', 'auto');
  };

  // tracking click voucher ticket or button show full voucher collect list
  const trackingClickCollectionVoucherSection = () => {
    const metaData = {
      customerId: String(user?.customerID),
      skuCode: sku,
      skuName,
      sellerCode,
      sellerName,
      source: 'web-desktop',
      screen: mapScreenToEnum({}, router.pathname),
      description: `Click vào bất kỳ đâu của section voucher ở trang chi tiết sản phẩm`,
    };
    MonitorUtils.collectVoucherClickEvent(MONITORING_COLLECTOR_TYPE.CLICK_COLLECTION_VOUCHER_SECTION, metaData);
  };
  // tracking button collect
  const trackingClickCollectionButton = (voucherCode: string) => {
    const metaData = {
      customerId: String(user?.customerID),
      voucherId: voucherCode,
      skuCode: sku,
      skuName,
      sellerCode,
      sellerName,
      source: 'web-desktop',
      screen: mapScreenToEnum({}, router.pathname),
      description: `Click vào nút “Thu thập”`,
    };
    MonitorUtils.collectVoucherClickEvent(MONITORING_COLLECTOR_TYPE.CLICK_COLLECTION_BUTTON, metaData);
  };
  // tracking button condition
  const trackingClickCollectionVoucherDetail = (voucherCode: string) => {
    const metaData = {
      customerId: String(user?.customerID),
      voucherId: voucherCode,
      skuCode: sku,
      skuName,
      sellerCode,
      sellerName,
      source: 'web-desktop',
      screen: mapScreenToEnum({}, router.pathname),
      description: `Click nút “Điều kiện” của card voucher`,
    };
    MonitorUtils.collectVoucherClickEvent(MONITORING_COLLECTOR_TYPE.CLICK_COLLECTION_VOUCHER_DETAIL, metaData);
  };
  // tracking button collect in condition popup
  const trackingClickCollectionVoucherButtonInDetailPopup = (voucherCode: string) => {
    const metaData = {
      customerId: String(user?.customerID),
      voucherId: voucherCode,
      skuCode: sku,
      skuName,
      sellerCode,
      sellerName,
      source: 'web-desktop',
      screen: mapScreenToEnum({}, router.pathname),
      description: `Click nút “Thu thập” trong popup điều kiện`,
    };
    MonitorUtils.collectVoucherClickEvent(MONITORING_COLLECTOR_TYPE.CLICK_COLLECTION_VOUCHER_BUTTON_IN_DETAIL_POPUP, metaData);
  };

  useEffect(() => {
    // TODO: check and show guide by tooltip
    const checkGuideVoucherCollection = async () => {
      const respCheckGuideCollectVoucher = await getUserLogGuide({
        q: {
          feature: GUIDE_FEATURE.COLLECTION_VOUCHER_GUIDE,
        },
      });
      if (respCheckGuideCollectVoucher?.status === 'OK') {
        const dataCheckGuide = getFirst(respCheckGuideCollectVoucher);
        if (dataCheckGuide?.isActive && !dataCheckGuide?.isGuided) {
          toggleGuidedTooltip(true);
          document.getElementById('marketPromoSection')?.style.setProperty('overflow-y', 'hidden');
        } else {
          toggleGuidedTooltip(false);
        }
      } else {
        toggleGuidedTooltip(false);
      }
    };

    if (isOpenDrawer && !isFirstLoading) {
      setTimeout(() => {
        // handle asynchronous
        checkGuideVoucherCollection();
      }, 300);
    }

    if (!isOpenDrawer) {
      setFirstEleOfAll(null); // clear state first element when close drawer
    }
  }, [isOpenDrawer, isFirstLoading]);

  useEffect(() => {
    // auto close guide tooltip after 3s
    if (isGuideTooltip) {
      setTimeout(() => {
        handleCloseGuideTooltip();
      }, timerOut);
    }
  }, [isGuideTooltip]);

  if (coupons?.length === 0) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={'12px'}>
      <Box minWidth="28%">
        <span className={styles.headLabel}>{headLabel}</span>
      </Box>
      <Box minWidth="55%" className={styles.listCouponWrapper}>
        <Slider className={styles.slider} {...settingCollectPromoCode}>
          {coupons?.map((coupon, idx) => <CouponWithText key={coupon.code} text={coupon.shortName} toggleDrawer={toggleDrawer} />)}
        </Slider>
      </Box>
      <Box minWidth="17%" marginLeft="auto">
        <Button className={clsx(styles.dFlex_spacing_4, styles.buttonAction)} onClick={toggleDrawer}>
          {actionLabel} {actionIcon}
        </Button>
      </Box>

      <RightDrawerListVoucher
        isOpen={isOpenDrawer}
        toggleDrawer={toggleDrawer}
        sellerCode={sellerCode}
        handleCollectVoucher={handleCollectVoucher}
        couponCollecting={couponCollecting}
        setCouponCollecting={setCouponCollecting}
        triggerRefetchVouchers={triggerRefetchVouchers}
        trackingClickCollectionVoucherDetail={trackingClickCollectionVoucherDetail}
        sku={sku}
        // guide tooltip
        isGuideTooltip={isGuideTooltip}
        handleCloseGuideTooltip={handleCloseGuideTooltip}
        isFirstLoading={isFirstLoading}
        setFirstLoading={setFirstLoading}
        firstEleOfAll={firstEleOfAll}
        setFirstEleOfAll={setFirstEleOfAll}
        vendorInfo={vendorInfo}
      />
    </Box>
  );
};

export default SlideCoupon;

const CouponWithText = ({ text, toggleDrawer }: { text: string; toggleDrawer: () => void }) => {
  return (
    <ButtonBase disableRipple onDragStart={(e) => e.preventDefault()} className={styles.couponWrapper} onClick={toggleDrawer}>
      {/* <div className={styles.couponMissingPart_left} /> */}
      <div className={styles.coupounPart_container}>{text}</div>
      {/* <div className={styles.couponMissingPart_right} /> */}
    </ButtonBase>
  );
};

const RightDrawerListVoucher = ({
  isOpen,
  toggleDrawer,
  sellerCode,
  handleCollectVoucher,
  couponCollecting,
  setCouponCollecting,
  triggerRefetchVouchers,
  trackingClickCollectionVoucherDetail,
  sku,
  isGuideTooltip = false,
  handleCloseGuideTooltip,
  isFirstLoading,
  setFirstLoading,
  firstEleOfAll,
  setFirstEleOfAll,
  vendorInfo,
}: {
  isOpen: boolean;
  toggleDrawer: () => void;
  sellerCode: string;
  handleCollectVoucher: ({ voucherCode, isPopupCondition }: { voucherCode: string; isPopupCondition: boolean }) => Promise<void>;
  couponCollecting: string | null;
  setCouponCollecting: Dispatch<SetStateAction<string | null>>;
  triggerRefetchVouchers: number;
  trackingClickCollectionVoucherDetail: (value: string) => void;
  sku: string;
  isGuideTooltip: boolean;
  handleCloseGuideTooltip: () => Promise<void>;
  isFirstLoading: boolean;
  setFirstLoading: Dispatch<SetStateAction<boolean>>;
  firstEleOfAll: string | null;
  setFirstEleOfAll: Dispatch<SetStateAction<string | null>>;
  vendorInfo: IVendorInfo;
}) => {
  const headerTitle = 'Mã giảm giá có thể sử dụng';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { user }: any = useAuth();

  const isInitialMount = useRef(true);
  const mountRef = useRef(false);

  // seller promo
  const [isSellerFetching, setSellerFetching] = useState(false); // indicator for see more button
  const [sellerPromoList, setSellerPromoList] = useState<IMyDataVoucher[]>([]);
  const [sellerPromoShowMore, setSellerPromoShowMore] = useState<IMyDataVoucher[]>([]);
  const [limitSellerPromo, setLimitSellerPromo] = useState(3);
  const [totalSellerPromo, setTotalSellerPromo] = useState(0);
  // market promo
  const [isMarketplaceFetching, setMarketFetching] = useState(false); // indicator for see more button
  const [marketplacePromoList, setMarketplacePromoList] = useState<IMyDataVoucher[]>([]);
  const [marketplacePromoShowMore, setMarketplacePromoShowMore] = useState<IMyDataVoucher[]>([]);
  const [limitMarketPromo, setLimitMarketPromo] = useState(3);
  const [totalMarketPromo, setTotalMarketPromo] = useState(0);

  const { open: isOpenCondition } = usePopupVoucherContext();

  // handle button see more voucher
  const handleFetchMore = (type: ENUM_SECTION_PROMO) => {
    if (type === ENUM_SECTION_PROMO.SELLER) {
      setSellerFetching(true);
      // button hide
      if (limitSellerPromo >= totalSellerPromo) {
        const prevSellerPromo = sellerPromoList?.map((item) => item).splice(0, 3);
        setSellerPromoShowMore(prevSellerPromo);
        setLimitSellerPromo(3);
        // button show more
      } else {
        setSellerPromoShowMore(sellerPromoList);
        setLimitSellerPromo(sellerPromoList?.length);
      }
      setSellerFetching(false);
    }

    if (type === ENUM_SECTION_PROMO.MARKETPLACE) {
      setMarketFetching(true);
      // button hide
      if (limitMarketPromo >= totalMarketPromo) {
        const prevSellerPromo = marketplacePromoList?.map((item) => item).splice(0, 3);
        setMarketplacePromoShowMore(prevSellerPromo);
        setLimitMarketPromo(3);
        // button show more
      } else {
        setMarketplacePromoShowMore(marketplacePromoList);
        setLimitMarketPromo(marketplacePromoList?.length);
      }
      setMarketFetching(false);
    }
  };

  useEffect(() => {
    async function getVoucherList() {
      if (!mountRef.current) {
        mountRef.current = true;
      }

      const querySellerPromos = {
        ctx: '',
        accountID: user?.accountId,
        scope: 'seller',
        sellerCode,
        getVoucherToCollect: true,
        skipVoucherCollected: false,
        storeCode: '',
        getMedxVoucherOnly: false,
        getTotal: true,
      };

      if (sellerCode === 'MEDX' && vendorInfo?.storeType === 'FLAGSHIP') {
        querySellerPromos.sellerCode = '';
        querySellerPromos.storeCode = vendorInfo.code;
      } else if (sellerCode === 'MEDX') {
        querySellerPromos.getMedxVoucherOnly = true;
      }

      const [respMarketplacePromos, respSellerPromos] = await Promise.all([
        postVoucherList({
          ctx: '',
          accountID: user?.accountId,
          scope: 'marketplace',
          sellerCode,
          getVoucherToCollect: true,
          skipVoucherCollected: false,
          getTotal: true,
        }),
        postVoucherList(querySellerPromos),
      ]);
      // check valid seller Promos
      if (isValid(respSellerPromos)) {
        const sellerPromos = getData(respSellerPromos);

        setSellerPromoList(sellerPromos);
        // TODO: if limitSellerPromo > 3, meaning list have expand, dont clear state
        if (limitSellerPromo > 3) {
          setSellerPromoShowMore(sellerPromos?.map((item: IMyDataVoucher) => item)?.splice(0, limitSellerPromo));
        } else {
          setSellerPromoShowMore(sellerPromos?.map((item: IMyDataVoucher) => item)?.splice(0, 3));
        }
        setTotalSellerPromo(respSellerPromos?.total || 0);
        if (!firstEleOfAll) {
          setFirstEleOfAll(getData(respSellerPromos)?.[0]?.code || null);
        }
      } else {
        setSellerPromoList([]);
        setSellerPromoShowMore([]);
        setTotalSellerPromo(0);
      }

      // check valid marketplace Promos
      if (isValid(respMarketplacePromos)) {
        setMarketplacePromoList(getData(respMarketplacePromos));
        // TODO: if limitMarketplacePromo > 3, meaning list have expand, dont clear state
        if (limitMarketPromo > 3) {
          setMarketplacePromoShowMore(
            getData(respMarketplacePromos)
              ?.map((item: IMyDataVoucher) => item)
              ?.splice(0, limitMarketPromo),
          );
        } else {
          setMarketplacePromoShowMore(
            getData(respMarketplacePromos)
              ?.map((item: IMyDataVoucher) => item)
              ?.splice(0, 3),
          );
        }
        setTotalMarketPromo(respMarketplacePromos?.total || 0);
        if (getData(respSellerPromos)?.length === 0) {
          setFirstEleOfAll(getData(respMarketplacePromos)?.[0]?.code || null);
        }
      } else {
        setMarketplacePromoList([]);
        setMarketplacePromoShowMore([]);
        setTotalMarketPromo(0);
      }

      setFirstLoading(false);
      // clear coupon collecting state
      setCouponCollecting(null);
    }

    if (!isInitialMount.current && isOpen) {
      getVoucherList();
    } else {
      isInitialMount.current = false;
    }
  }, [isOpen, triggerRefetchVouchers]);

  useEffect(() => {
    if (!isOpen) {
      // clear state show more
      setLimitSellerPromo(3);
      setTotalSellerPromo(0);
      setLimitMarketPromo(3);
      setTotalMarketPromo(0);
      // check if on Guiding, guided tooltip when close drawer
      if (isGuideTooltip) {
        handleCloseGuideTooltip();
      }
    }
  }, [isOpen, isGuideTooltip]);

  // TODO: if one of both section not available, will expand all another section
  const isMarketplaceExpandAll = sellerPromoList?.length === 0;
  const marketplaceVoucherList = isMarketplaceExpandAll ? marketplacePromoList : marketplacePromoShowMore;
  const isSellerExpandAll = marketplacePromoList?.length === 0;
  const sellerVoucherList = isSellerExpandAll ? sellerPromoList : sellerPromoShowMore;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={toggleDrawer}
      PaperProps={{
        style: {
          width: '30%',
          minWidth: 436,
          display: isOpenCondition ? 'none' : 'flex',
        },
      }}
    >
      <HeaderDrawer title={headerTitle} toggleDrawer={toggleDrawer} />
      <div className={styles.couponListWrapper} id="marketPromoSection">
        {isFirstLoading ? (
          <Grid className={styles.loadLogo}>
            <LoadingBM />
          </Grid>
        ) : (
          <div
            className={clsx(styles.couponList, marketplacePromoList?.length <= 0 && sellerPromoList?.length <= 0 ? styles.unScrollY : null)}
            id="sellerSectionPromoList"
          >
            {sellerPromoList?.length === 0 ? null : (
              <div className={styles.sectionVoucher_sticky}>
                <Typography className={styles.sectionVoucher_title}>Mã giảm giá từ nhà bán hàng</Typography>
              </div>
            )}
            <Grid container className={clsx(styles.voucherList_container, sellerPromoList?.length === 0 ? styles.displayNone : null)}>
              {sellerVoucherList?.map((coupon, idx) => (
                <Grid className={styles.coupon_card_grid} item key={coupon?.code} style={{ width: '100%' }}>
                  <DrawerVoucherTicket
                    key={coupon?.code}
                    coupon={coupon}
                    voucherSource={ENUM_SECTION_PROMO.SELLER}
                    handleCollectVoucher={handleCollectVoucher}
                    couponCollecting={couponCollecting}
                    trackingClickCollectionVoucherDetail={trackingClickCollectionVoucherDetail}
                    isGuideTooltip={isGuideTooltip}
                    firstEleOfAll={firstEleOfAll}
                    handleCloseGuideTooltip={handleCloseGuideTooltip}
                  />
                </Grid>
              ))}
            </Grid>
            {!isSellerExpandAll && sellerPromoList?.length > 3 ? (
              <div className={styles.buttonCollapse}>
                <SeeMoreBtn
                  type={ENUM_SECTION_PROMO.SELLER}
                  handleFetchMore={() => handleFetchMore(ENUM_SECTION_PROMO.SELLER)}
                  isFetching={isSellerFetching}
                  isReverse={limitSellerPromo >= totalSellerPromo}
                />
              </div>
            ) : null}
            {/* divider */}
            {marketplacePromoList?.length > 0 && sellerPromoList?.length > 0 ? <div className={styles.newDivider} /> : null}
            {/* all marketplace promo */}
            {marketplacePromoList?.length > 0 ? (
              <div className={clsx(styles.counpon_list)} id="marketSectionPromoList">
                <div className={styles.sectionVoucher_sticky}>
                  <Typography className={styles.sectionVoucher_title}>Mã giảm giá từ Buymed</Typography>
                </div>
                <Grid container className={clsx(styles.voucherList_container, marketplacePromoList?.length === 0 ? styles.displayNone : null)}>
                  {marketplaceVoucherList?.map((coupon) => (
                    <Box width="100%" marginBottom="12px" key={coupon?.code}>
                      <Grid className={styles.coupon_card_grid} item key={coupon?.code} style={{ width: '100%' }} data-test="modal-item-promo">
                        <DrawerVoucherTicket
                          key={coupon?.code}
                          coupon={coupon}
                          voucherSource={ENUM_SECTION_PROMO.MARKETPLACE}
                          handleCollectVoucher={handleCollectVoucher}
                          couponCollecting={couponCollecting}
                          trackingClickCollectionVoucherDetail={trackingClickCollectionVoucherDetail}
                          isGuideTooltip={isGuideTooltip}
                          firstEleOfAll={firstEleOfAll}
                          handleCloseGuideTooltip={handleCloseGuideTooltip}
                        />
                      </Grid>
                    </Box>
                  ))}
                </Grid>
                {!isMarketplaceExpandAll && marketplacePromoList?.length > 3 ? (
                  <div className={styles.buttonCollapse}>
                    <SeeMoreBtn
                      type={ENUM_SECTION_PROMO.MARKETPLACE}
                      handleFetchMore={() => handleFetchMore(ENUM_SECTION_PROMO.MARKETPLACE)}
                      isFetching={isMarketplaceFetching}
                      isReverse={limitMarketPromo >= totalMarketPromo}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Drawer>
  );
};

// voucher ticket inside drawer
const DrawerVoucherTicket = ({
  coupon,
  voucherSource,
  handleCollectVoucher,
  couponCollecting,
  trackingClickCollectionVoucherDetail,
  isGuideTooltip = false,
  firstEleOfAll,
  handleCloseGuideTooltip,
}: {
  coupon: IMyDataVoucher;
  voucherSource: ENUM_SECTION_PROMO;
  handleCollectVoucher: ({ voucherCode, isPopupCondition }: { voucherCode: string; isPopupCondition: boolean }) => Promise<void>;
  couponCollecting: string | null;
  trackingClickCollectionVoucherDetail: (value: string) => void;
  isGuideTooltip: boolean;
  firstEleOfAll: string | null;
  handleCloseGuideTooltip: () => Promise<void>;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { endTime, code = '', collectStatus } = coupon || {};

  const { handleOpen } = usePopupVoucherContext();

  const checkTimeCountDown = () => {
    const threeDay = new Date().getTime() + 3 * 24 * 60 * 60 * 1000;
    const endTimeVoucher = new Date(endTime).getTime();
    return endTime && threeDay > endTimeVoucher ? (
      <span style={{ color: '#D4323B' }}>
        <CountdownTimerVoucher prefix="" dealEndDay={endTime} style={{ display: 'inline-block' }} typeSpace=", " />
      </span>
    ) : (
      <>
        HSD: <span>{DateTimeUtils.getFormattedWithDate(new Date(endTime), 'DD/MM/YYYY')}</span>
      </>
    );
  };

  const handleOpenVoucher = () => {
    handleOpen(coupon.code);
    trackingClickCollectionVoucherDetail(code);
  };

  if (voucherSource === ENUM_SECTION_PROMO.MARKETPLACE) {
    return (
      <Box className={styles.voucherTicket_container}>
        <div className={styles.voucherTicketMissingPart_topMarketplace} />
        <Grid container className={styles.voucherTicket_childContainerMarketplace} style={{ height: '100%' }}>
          <Grid item md={8} className={styles.voucherTicket_leftContainer}>
            <Box>
              <div className={styles.voucherTicket_nameMarketplace}>{coupon.shortName}</div>
              <div className={styles.content_2Line} dangerouslySetInnerHTML={{ __html: coupon?.description }} />
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography className={styles.labelExpiredDate}>{checkTimeCountDown()}</Typography>
              <Button className={styles.buttonCondition} onClick={handleOpenVoucher}>
                <ErrorOutline style={{ width: '20px', height: '20px' }} />
                <Typography variant="body2" className={styles.labelCondition}>
                  Điều kiện
                </Typography>
              </Button>
            </Box>
          </Grid>
          <div className={styles.verticalDivider} />
          <Grid item md={4} className={styles.buttonCollectWrapper}>
            <ButtonCollect
              couponCode={coupon?.code}
              couponCollecting={couponCollecting}
              handleCollectVoucher={handleCollectVoucher}
              collectStatus={collectStatus}
              voucherSource={ENUM_SECTION_PROMO.MARKETPLACE}
              isGuideTooltip={isGuideTooltip}
              firstEleOfAll={firstEleOfAll}
              handleCloseGuideTooltip={handleCloseGuideTooltip}
            />
          </Grid>
        </Grid>
        <div className={styles.voucherTicketMissingPart_bottomMarketplace} />
      </Box>
    );
  }

  // voucher Seller
  return (
    <Box className={styles.voucherTicket_container}>
      <div className={styles.voucherTicketMissingPart_top} />
      <Grid container className={styles.voucherTicket_childContainer}>
        <Grid item md={8} className={styles.voucherTicket_leftContainer}>
          <Box>
            <div className={styles.voucherTicket_name}>{coupon.shortName}</div>
            <div className={styles.content_2Line} dangerouslySetInnerHTML={{ __html: coupon?.description }} />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography className={styles.labelExpiredDate}>{checkTimeCountDown()}</Typography>
            <Button className={styles.buttonCondition} onClick={handleOpenVoucher}>
              <ErrorOutline style={{ width: '20px', height: '20px' }} />
              <Typography variant="body2" className={styles.labelCondition}>
                Điều kiện
              </Typography>
            </Button>
          </Box>
        </Grid>
        <div className={styles.verticalDivider} />
        <Grid item md={4} className={styles.buttonCollectWrapper}>
          <ButtonCollect
            couponCode={coupon?.code}
            couponCollecting={couponCollecting}
            handleCollectVoucher={handleCollectVoucher}
            collectStatus={collectStatus}
            voucherSource={ENUM_SECTION_PROMO.SELLER}
            isGuideTooltip={isGuideTooltip}
            firstEleOfAll={firstEleOfAll}
            handleCloseGuideTooltip={handleCloseGuideTooltip}
          />
        </Grid>
      </Grid>
      <div className={styles.voucherTicketMissingPart_bottom} />
    </Box>
  );
};

// Button expand/collapse voucher list
const SeeMoreBtn = ({
  isFetching,
  handleFetchMore,
  type,
  isReverse = false,
}: {
  isFetching: boolean;
  handleFetchMore: (type: ENUM_SECTION_PROMO) => void;
  type: ENUM_SECTION_PROMO;
  isReverse: boolean;
}) => {
  return (
    <Button className={styles.btnSeeMoreItem} onClick={() => handleFetchMore(type)}>
      {isFetching ? (
        <CircularProgress color="primary" size={24} />
      ) : (
        <>
          {isReverse ? 'Ẩn' : 'Xem thêm'}{' '}
          {isReverse ? <ExpandLess style={{ width: 24, height: 24 }} /> : <ExpandMore style={{ width: 24, height: 24 }} />}
        </>
      )}
    </Button>
  );
};

// Drawer Head title
const HeaderDrawer = ({ title, toggleDrawer }: { title: string; toggleDrawer: () => void }) => {
  return (
    <Box minWidth={436}>
      <Box display="flex" justifyContent="space-between" alignItems="center" padding="16px 20px">
        <span className={styles.drawerHeadTitle}>{title}</span>
        <IconButton onClick={toggleDrawer} style={{ padding: 5 }}>
          <Close style={{ width: 20, height: 20, strokeWidth: 1, stroke: '#797979' }} />
        </IconButton>
      </Box>
      <Divider />
    </Box>
  );
};

const ButtonCollect = ({
  voucherSource,
  collectStatus,
  handleCollectVoucher,
  couponCollecting,
  couponCode,
  isGuideTooltip = false,
  handleCloseGuideTooltip,
  firstEleOfAll,
}: {
  voucherSource: ENUM_SECTION_PROMO;
  collectStatus: ENUM_VOUCHER_COLLECTION_STATUS;
  handleCollectVoucher: ({ voucherCode, isPopupCondition }: { voucherCode: string; isPopupCondition: boolean }) => Promise<void>;
  couponCollecting: string | null;
  couponCode: string;
  isGuideTooltip: boolean;
  handleCloseGuideTooltip: () => Promise<void>;
  firstEleOfAll: string | null;
}) => {
  const contentGuideTooltip = (
    <Box display="flex" flexDirection="column" fontSize="14px" gridGap="8px" lineHeight="18px">
      <strong>Bấm Thu thập để lấy mã giảm giá này. </strong>
      <span>Sau khi thu thập, mã sẽ được lưu tại trang mã giảm giá để bạn tiện theo dõi và sử dụng.</span>
    </Box>
  );

  if (isGuideTooltip) {
    if (firstEleOfAll === couponCode && voucherSource === ENUM_SECTION_PROMO.SELLER) {
      return (
        <ClickAwayListener onClickAway={handleCloseGuideTooltip}>
          <Tooltip
            open={isGuideTooltip}
            title={contentGuideTooltip}
            arrow
            classes={{
              tooltip: styles.tooltipGuide,
            }}
          >
            <Box>
              {collectStatus === ENUM_VOUCHER_COLLECTION_STATUS.COLLECTED ? (
                <Box display="flex" gap={'4px'} justifyContent="center" alignItems="center">
                  <ImageFallbackStatic src={COLLECT_VOUCHER_COLLECTED_BLUE} width={14} height={14} alt="voucher seller collected" />
                  <span className={styles.textCollectedSeller}>Đã thu thập</span>
                </Box>
              ) : (
                <Button
                  className={styles.sellerButtonCollect}
                  onClick={() => handleCollectVoucher({ voucherCode: couponCode, isPopupCondition: false })}
                  disabled={couponCollecting !== null}
                >
                  {couponCollecting === couponCode ? <CircularProgress size={16} className={styles.whiteProgressColor} /> : 'Thu thập'}
                </Button>
              )}
            </Box>
          </Tooltip>
        </ClickAwayListener>
      );
    }

    if (firstEleOfAll === couponCode && voucherSource === ENUM_SECTION_PROMO.MARKETPLACE) {
      return (
        <ClickAwayListener onClickAway={handleCloseGuideTooltip}>
          <Tooltip open={isGuideTooltip} title={contentGuideTooltip} arrow>
            <Box>
              {collectStatus === ENUM_VOUCHER_COLLECTION_STATUS.COLLECTED ? (
                <Box display="flex" gap={'4px'} justifyContent="center" alignItems="center">
                  <ImageFallbackStatic src={COLLECT_VOUCHER_COLLECTED_GREEN} width={14} height={14} alt="voucher market collected" />
                  <span className={styles.textCollectedMarketplace}>Đã thu thập</span>
                </Box>
              ) : (
                <Button
                  className={styles.marketplaceButtonCollect}
                  onClick={() => handleCollectVoucher({ voucherCode: couponCode, isPopupCondition: false })}
                  disabled={couponCollecting !== null}
                >
                  {couponCollecting === couponCode ? <CircularProgress size={16} className={styles.whiteProgressColor} /> : 'Thu thập'}
                </Button>
              )}
            </Box>
          </Tooltip>
        </ClickAwayListener>
      );
    }
  }

  if (collectStatus === ENUM_VOUCHER_COLLECTION_STATUS.OWNERSHIP) {
    return (
      <Box>
        <Box display="flex" gap={'4px'} justifyContent="center" alignItems="center">
          <ImageFallbackStatic src={COLLECT_VOUCHER_COLLECTED_GREY} width={14} height={14} alt="voucher owner" />
          <span className={styles.textOwnerVoucher}>Đã sở hữu</span>
        </Box>
      </Box>
    );
  }

  if (voucherSource === ENUM_SECTION_PROMO.MARKETPLACE) {
    return (
      <Box>
        {collectStatus === ENUM_VOUCHER_COLLECTION_STATUS.COLLECTED ? (
          <Box display="flex" gap={'4px'} justifyContent="center" alignItems="center">
            <ImageFallbackStatic src={COLLECT_VOUCHER_COLLECTED_GREEN} width={14} height={14} alt="voucher market collected" />
            <span className={styles.textCollectedMarketplace}>Đã thu thập</span>
          </Box>
        ) : (
          <Button
            className={styles.marketplaceButtonCollect}
            onClick={() => handleCollectVoucher({ voucherCode: couponCode, isPopupCondition: false })}
            disabled={couponCollecting !== null}
          >
            {couponCollecting === couponCode ? <CircularProgress size={16} className={styles.whiteProgressColor} /> : 'Thu thập'}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {collectStatus === ENUM_VOUCHER_COLLECTION_STATUS.COLLECTED ? (
        <Box display="flex" gap={'4px'} justifyContent="center" alignItems="center">
          <ImageFallbackStatic src={COLLECT_VOUCHER_COLLECTED_BLUE} width={14} height={14} alt="voucher seller collected" />
          <span className={styles.textCollectedSeller}>Đã thu thập</span>
        </Box>
      ) : (
        <Button
          className={styles.sellerButtonCollect}
          onClick={() => handleCollectVoucher({ voucherCode: couponCode, isPopupCondition: false })}
          disabled={couponCollecting !== null}
        >
          {couponCollecting === couponCode ? <CircularProgress size={16} className={styles.whiteProgressColor} /> : 'Thu thập'}
        </Button>
      )}
    </Box>
  );
};
